// Controls Panel Component
// Organized for Supabase Migration

import React from 'react';
import { Ruler, Camera, Zap } from 'lucide-react';
import { PanControl } from './PanControl.jsx';
import { AlignPanel } from './AlignPanel.jsx';

export const ControlsPanel = ({ 
  showCoordinates, 
  setShowCoordinates, 
  showGrid, 
  setShowGrid,
  imageInputRef,
  handleImageUpload,
  isAnalyzing,
  uploadedImage,
  viewportWidth,
  setViewportWidth,
  viewportHeight,
  setViewportHeight,
  viewportScale,
  setViewportScale,
  currentSketch,
  getCurrentParams,
  onParamsUpdate,
  getSketchPoints,
  onAlignComplete
}) => {
  return (
    <div className="lg:col-span-1">
      {/* Controls */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Ruler className="w-5 h-5" />
          Controls
        </h3>
        <div className="space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showCoordinates}
              onChange={(e) => setShowCoordinates(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Show Coordinates</span>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showGrid}
              onChange={(e) => setShowGrid(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Show Grid</span>
          </label>
        </div>

        {/* Viewport Controls */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Viewport Controls</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Width: {viewportWidth}</label>
              <input
                type="range"
                min="200"
                max="800"
                step="50"
                value={viewportWidth}
                onChange={(e) => setViewportWidth(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Height: {viewportHeight}</label>
              <input
                type="range"
                min="150"
                max="600"
                step="50"
                value={viewportHeight}
                onChange={(e) => setViewportHeight(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Scale: {viewportScale}x</label>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.1"
                value={viewportScale}
                onChange={(e) => setViewportScale(parseFloat(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setViewportWidth(400);
                  setViewportHeight(300);
                  setViewportScale(1);
                }}
                className="flex-1 text-xs bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded transition-colors"
              >
                Reset
              </button>
              <button
                onClick={() => {
                  setViewportWidth(600);
                  setViewportHeight(450);
                  setViewportScale(1.2);
                }}
                className="flex-1 text-xs bg-blue-200 hover:bg-blue-300 px-2 py-1 rounded transition-colors"
              >
                Zoom Out
              </button>
            </div>
          </div>
        </div>
        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-700">
            <strong>💡 Tip:</strong> Drag the red points to move them interactively!
          </p>
        </div>
      </div>

      {/* Image Upload */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-4 mb-4">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Camera className="w-5 h-5" />
          Image to Sketch
        </h3>
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
          className="hidden"
        />
        <button
          onClick={() => imageInputRef.current?.click()}
          disabled={isAnalyzing}
          className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-green-600 to-blue-600 text-white px-4 py-3 rounded-lg hover:from-green-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isAnalyzing ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Analyzing...
            </>
          ) : (
            <>
              <Zap className="w-4 h-4" />
              Upload & Generate
            </>
          )}
        </button>
        {uploadedImage && (
          <div className="mt-3">
            <p className="text-sm text-gray-600 mb-2">Uploaded Image:</p>
            <img src={uploadedImage} alt="Uploaded" className="w-full h-20 object-cover rounded border" />
          </div>
        )}
      </div>

      {/* Pan Control */}
      {currentSketch && (
        <div className="mb-4">
          <PanControl
            currentSketch={currentSketch}
            getCurrentParams={getCurrentParams}
            onParamsUpdate={onParamsUpdate}
            viewportWidth={viewportWidth}
            viewportHeight={viewportHeight}
            viewportScale={viewportScale}
          />
        </div>
      )}

      {/* Align Panel */}
      {currentSketch && (
        <div className="mb-4">
          <AlignPanel
            currentSketch={currentSketch}
            getCurrentParams={getCurrentParams}
            getSketchPoints={getSketchPoints}
            onAlignComplete={onAlignComplete}
            viewportWidth={viewportWidth}
            viewportHeight={viewportHeight}
            viewportScale={viewportScale}
          />
        </div>
      )}
    </div>
  );
};
